import { Form } from 'components/form'
import { useAlert } from 'contexts/alert'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import api from 'services/api'

import { ajvResolver } from '@hookform/resolvers/ajv'
import InvoiceSchema, { IInvoiceFormData } from 'schema/comercial/invoice'
import { useStyles } from './styles'

interface IRouteParams {
  id: string
}

const InvoiceForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [readonly, setReadonly] = useState(false)
  const [commercialPlansA, setCommercialPlansA] = useState([])
  const [paymentGatewaysB, setPaymentGatewaysB] = useState([])
  const { closeAlert, openAlert } = useAlert()
  const params = useParams<IRouteParams>()
  const firstInputElement = useRef(null)
  const classes = useStyles()
  const history = useHistory()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
    control,
    setFocus,
  } = useForm<IInvoiceFormData>({
    resolver: ajvResolver(InvoiceSchema),
    defaultValues: {
      year: '',
      month: '',
      amount: 0,
      dueDate: '',
      payDate: undefined,
      amountPaid: 0,
      commercialPlanId: '',
      paymentGatewayId: '',
      paymentTransaction: '',
      paymentMethod: '',
      paymentCurrency: '',
    },
  })

  const handleChange = (formField: any) => closeAlert()

  // main data load
  useEffect(() => {
    async function loadData() {
      const { id } = params

      // form data
      await api
        .get(`/invoices/${id}`)
        .then((response) => {
          const { data } = response.data

          const invoiceResult: IInvoiceFormData = {
            id: data.id,
            year: data.year,
            month: data.month,
            amount: data.amount,
            dueDate: data.dueDate,
            payDate: data.payDate,
            amountPaid: data.amountPaid,
            commercialPlanId: data.commercialPlanId.id,
            paymentGatewayId: data.paymentGatewayId.id,
            paymentTransaction: data.paymentTransaction,
            paymentMethod: data.paymentMethod,
            paymentCurrency: data.paymentCurrency,
          }

          return invoiceResult
        })
        .then((invoiceResult: IInvoiceFormData) => {
          reset(invoiceResult)
        })
        .catch((error) => {
          console.log(error)
          return error
        })
    }

    if (params.id) {
      loadData()
      setReadonly(true)
    }
  }, [params, params.id])

  // data save
  const onSubmit: SubmitHandler<IInvoiceFormData> = useCallback(async (data) => {
    setIsLoading(true)

    const payLoad: IInvoiceFormData = {
      id: data.id,
      year: data.year,
      month: data.month,
      amount: data.amount,
      dueDate: data.dueDate,
      payDate: data.payDate,
      amountPaid: data.amountPaid,
      commercialPlanId: data.commercialPlanId,
      paymentGatewayId: data.paymentGatewayId,
      paymentTransaction: data.paymentTransaction,
      paymentMethod: data.paymentMethod,
      paymentCurrency: data.paymentCurrency,
    }

    if (params.id) {
      const { id } = params

      payLoad.id = id

      await api
        .put(`/invoices`, payLoad)
        .then(() => {
          openAlert('Cliente atualizado com sucesso!', 'success')
          history.push('/invoices')
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    } else {
      await api
        .post('/invoices', payLoad)
        .then(() => {
          openAlert('Dado cadastrado com sucesso!', 'success')
          reset()
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    }
  }, [])

  return (
    <Form.Root onHandleSubmit={handleSubmit(onSubmit)}>
      <Form.Info
        title='Faturamento'
        subTitle='Dados coletados sobre faturamento.'
      />
      <Form.Body>
        <Form.Input
          label='Ano'
          error={!!errors.year}
          errorMessage={errors?.year?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          maxLength={4}
          name='year'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Mês'
          error={!!errors.month}
          errorMessage={errors?.month?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          maxLength={2}
          name='month'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Valor'
          error={!!errors.amount}
          errorMessage={errors?.amount?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          maxLength={10}
          name='amount'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='number'
        />

        <Form.Input
          label='Data de Vencimento'
          error={!!errors.dueDate}
          errorMessage={errors?.dueDate?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          maxLength={10}
          name='dueDate'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='date'
        />

        <Form.Input
          label='Data de Pagamento'
          error={!!errors.payDate}
          errorMessage={errors?.payDate?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          maxLength={10}
          name='payDate'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='date'
        />

        <Form.Input
          label='Valor Pago'
          error={!!errors.amountPaid}
          errorMessage={errors?.amountPaid?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          maxLength={10}
          name='amountPaid'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
          type='number'
        />

        <Form.Select
          label='Plano Comercial'
          error={!!errors.commercialPlanId}
          errorMessage={errors?.commercialPlanId?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          name='commercialPlanId'
          rules={{
            onChange: (e) => {
              setValue('commercialPlanId', e.target.value)
              handleChange(e)
            },
          }}
          register={register}
          value={`${watch().commercialPlanId}`}
          options={commercialPlansA}
        />

        <Form.Select
          label='Gateway de Pagamento'
          error={!!errors.paymentGatewayId}
          errorMessage={errors?.paymentGatewayId?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 3, lg: 3, xl: 3 }}
          name='paymentGatewayId'
          rules={{
            onChange: (e) => {
              setValue('paymentGatewayId', e.target.value)
              handleChange(e)
            },
          }}
          register={register}
          value={`${watch().paymentGatewayId}`}
          options={paymentGatewaysB}
        />

        <Form.Input
          label='Id da Transação'
          error={!!errors.paymentTransaction}
          errorMessage={errors?.paymentTransaction?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 3, lg: 3, xl: 3 }}
          maxLength={50}
          name='paymentTransaction'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Método de Pagamento'
          error={!!errors.paymentMethod}
          errorMessage={errors?.paymentMethod?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 3, lg: 3, xl: 3 }}
          maxLength={20}
          name='paymentMethod'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Input
          label='Moeda do Pagamento'
          error={!!errors.paymentCurrency}
          errorMessage={errors?.paymentCurrency?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 3, lg: 3, xl: 3 }}
          maxLength={20}
          name='paymentCurrency'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />
      </Form.Body>

      <Form.Divisor />

      <Form.Footer>
        <Form.Button
          label='SALVAR'
          type='submit'
          variant='contained'
          isLoading={isLoading}
        />
        <Form.Button
          label='CANCELAR'
          type='cancel'
          variant='text'
          isLoading={isLoading}
        />
      </Form.Footer>
    </Form.Root>
  )
}

export default InvoiceForm
